import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Home } from 'lucide-react';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

/**
 * Autocomplete d'adresse postale via la BAN (api-adresse.data.gouv.fr).
 * Lorsqu'une suggestion est cliquée, onSelect reçoit :
 *   { name, city, postal_code, citycode, lat, lon, label }
 * Cela permet de récupérer une adresse précise pour les analyses
 * Cadastre / PLU / DVF / Risques (géolocalisées).
 */
export const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = "Saisissez l'adresse... (ex. 12 rue de la Paix)",
  className = "",
  required = false,
}) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAddresses = async (q) => {
    if (q.length < 3) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/utils/addresses?q=${encodeURIComponent(q)}`, { withCredentials: true });
      setSuggestions(res.data || []);
      setOpen(true);
    } catch { setSuggestions([]); }
    finally { setLoading(false); }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange?.(val);
    setHighlight(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchAddresses(val), 250);
  };

  const handleSelect = (addr) => {
    // On ne stocke dans le champ adresse que la rue (ex "12 rue de la Paix"),
    // la ville et le CP sont remontés via onSelect pour remplir d'autres champs.
    const street = addr.name || addr.label || '';
    setQuery(street);
    setSuggestions([]);
    setOpen(false);
    onChange?.(street);
    onSelect?.(addr);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && highlight >= 0) { e.preventDefault(); handleSelect(suggestions[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          value={query}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          className="pl-9 bg-zinc-950 border-zinc-800 text-white"
          data-testid="address-autocomplete-input"
          autoComplete="off"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl overflow-hidden max-h-72 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={`${s.label}-${i}`}
              type="button"
              onMouseEnter={() => setHighlight(i)}
              onClick={() => handleSelect(s)}
              className={`w-full text-left px-3 py-2 hover:bg-zinc-800 transition-colors flex items-center gap-3 border-b border-zinc-800 last:border-0 ${highlight === i ? 'bg-zinc-800' : ''}`}
              data-testid={`address-suggestion-${i}`}
            >
              <Home className={`w-4 h-4 flex-shrink-0 ${s.type === 'housenumber' ? 'text-emerald-500' : 'text-zinc-500'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{s.name || s.label}</p>
                <p className="text-xs text-zinc-500 truncate">
                  {s.postal_code} {s.city}{s.context ? ` · ${s.context}` : ''}
                </p>
              </div>
              {s.type === 'housenumber' && (
                <span className="text-[10px] text-emerald-500 font-mono uppercase tracking-wider flex-shrink-0">précise</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
